import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  ComplianceStatus,
  ComplianceStatusFilter,
  ComplianceTasksTableRowAction,
  ComplianceTaskType,
  RoutineComplianceTask,
  TableColumn,
} from '../../../compliance-overview/models/compliance-item.model';
import { resolveInspectionTypeId } from '../../../compliance-overview/models/routine-video.model';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { ComplianceFiltersComponent } from '../compliance-filters/compliance-filters.component';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { ComplianceTasksTableAction } from '../../enums/compliance-tasks-table-action.enum';
import {
  routineChecklistEntityKindFromLabel,
  routineChecklistTabTitleKeys
} from '../../models/routine-checklist-display.model';

@Component({
  selector: 'app-compliance-tasks',
  standalone: true,
  imports: [
    CommonModule,
    SearchComponent,
    ComplianceFiltersComponent,
    NgbDropdownModule,
    ScrollingModule,
    EmptyStateComponent,
    ErrorStateComponent,
    TranslocoPipe
  ],
  templateUrl: './compliance-tasks-table.component.html',
  styleUrl: './compliance-tasks-table.component.scss'
})
export class ComplianceTasksTableComponent {
  private readonly transloco = inject(TranslocoService);

  @ViewChild('tableContainer') tableContainer!: ElementRef<HTMLElement>;
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('expandableViewport') expandableViewport?: ElementRef<HTMLElement>;
  @ViewChildren(NgbDropdown) dropdowns!: QueryList<NgbDropdown>;

  @Input() taskType: ComplianceTaskType = 'periodic';
  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  @Input() isLoading: boolean = false;
  @Input() isLoadingMore: boolean = false;
  @Input() isRefreshing: boolean = false;
  @Input() hasError: boolean = false;
  @Input() minWidth: number = 900;
  @Input() isComplianceTasks: boolean = false;
  @Input() isRequirementAssetList: boolean = false;
  @Input() isComplianceLibrary: boolean = false;
  @Input() isAssignedAssets: boolean = false;
  @Input() isAssetHistory: boolean = false;
  @Input() isArchivedBtn: boolean = false;
  @Input() isChecklistRequirements: boolean = false;
  @Input() expandable: boolean = false;
  @Input() subColumns: TableColumn[] = [];
  @Input() subRowsKey: string = 'legs';
  @Input() subRowActionsKey: string = 'actions';
  @Input() rowIdKey: string = 'id';
  @Input() subRowsEmptyMessage: string = this.transloco.translate('compliance.shared.tasksTable.noDetailsToDisplay');

  @Output() rowClicked = new EventEmitter<any>();
  @Output() actionClicked = new EventEmitter<any>();
  @Output() linkClicked = new EventEmitter<{ item: any; column: TableColumn }>();
  @Output() assignClicked = new EventEmitter<any>();
  @Output() archiveClicked = new EventEmitter<any>();
  @Output() restoreClicked = new EventEmitter<any>();
  @Output() historyClicked = new EventEmitter<any>();
  @Output() editRequirementsClicked = new EventEmitter<any>();
  @Output() editFrequencyClicked = new EventEmitter<any>();
  @Output() editClicked = new EventEmitter<any>();
  @Output() rescheduleClicked = new EventEmitter<any>();
  @Output() reschedulePreClicked = new EventEmitter<any>();
  @Output() reschedulePostClicked = new EventEmitter<any>();
  @Output() deleteClicked = new EventEmitter<any>();
  @Output() assignWorkshopClicked = new EventEmitter<any>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() statusFilterChanged = new EventEmitter<ComplianceStatusFilter>();
  @Output() checkboxChanged = new EventEmitter<any>();
  @Output() scrolledDown = new EventEmitter<void>();

  private expandableScrollThreshold:number = 200;

  searchTerm: string = '';
  readonly expandedIds = new Set<any>();
  tableAction = ComplianceTasksTableAction;
  complianceStatus = ComplianceStatusFilter;
  selectedComplianceStatus: ComplianceStatusFilter = ComplianceStatusFilter.All;
  complianceStatusLabelKeys: Record<ComplianceStatusFilter, string> = {
    [ComplianceStatusFilter.All]: 'compliance.shared.tasksTable.statusFilter.all',
    [ComplianceStatusFilter.Pending]: 'compliance.shared.tasksTable.statusFilter.pending',
    [ComplianceStatusFilter.InProgress]: 'compliance.shared.tasksTable.statusFilter.inProgress',
    [ComplianceStatusFilter.Completed]: 'compliance.shared.tasksTable.statusFilter.completed',
    [ComplianceStatusFilter.Overdue]: 'compliance.shared.tasksTable.statusFilter.overdue',
    [ComplianceStatusFilter.Missed]: 'compliance.shared.tasksTable.statusFilter.missed'
  }
  statusLabels: Record<ComplianceStatus, string> = {
    [ComplianceStatus.Pending]: 'Pending',
    [ComplianceStatus.InProgress]: 'In-Progress',
    [ComplianceStatus.Completed]: 'Completed',
    [ComplianceStatus.Overdue]: 'Overdue',
    [ComplianceStatus.Missed]: 'Missed'
  }
  statusLabelKeys: Record<ComplianceStatus, string> = {
    [ComplianceStatus.Pending]: 'compliance.shared.tasksTable.status.pending',
    [ComplianceStatus.InProgress]: 'compliance.shared.tasksTable.status.inProgress',
    [ComplianceStatus.Completed]: 'compliance.shared.tasksTable.status.completed',
    [ComplianceStatus.Overdue]: 'compliance.shared.tasksTable.status.overdue',
    [ComplianceStatus.Missed]: 'compliance.shared.tasksTable.status.missed'
  }

  private readonly textStatusLabelKeys: Record<string, string> = {
    'Compliant': 'compliance.shared.tasksTable.textStatus.compliant',
    'Non-Compliant': 'compliance.shared.tasksTable.textStatus.nonCompliant',
    'Non-compliant': 'compliance.shared.tasksTable.textStatus.nonCompliantSentenceCase',
    'Expiring soon': 'compliance.shared.tasksTable.textStatus.expiringSoon',
    'In Progress': 'compliance.shared.tasksTable.textStatus.inProgress',
    'Missed': 'compliance.shared.tasksTable.textStatus.missed',
    'Pass': 'compliance.shared.tasksTable.textStatus.pass',
    'Fail': 'compliance.shared.tasksTable.textStatus.fail',
    'Pending': 'compliance.shared.tasksTable.textStatus.pending'
  }

  get complianceStatusLabel(): string {
    return this.transloco.translate(this.complianceStatusLabelKeys[this.selectedComplianceStatus]);
  }

  get rowsClickable(): boolean {
    return this.rowClicked.observed;
  }

  getDropdownPopperOptions = (options: any): any => {
    const boundary = this.tableContainer?.nativeElement;
    if (!boundary) return options;

    const modifiers = options.modifiers ?? [];
    const boundaryModifiers = ['preventOverflow', 'flip'];
    const withBoundary = modifiers.map((modifier: any) => boundaryModifiers.includes(modifier.name)
      ? {
        ...modifier,
        options: {
          ...(modifier.options ?? {}),
          boundary
        }
      }
      : modifier
    );

    boundaryModifiers.forEach((name) => {
      if (!withBoundary.some((modifier: any) => modifier.name === name)) {
        withBoundary.push({
          name,
          options: { boundary }
        });
      }
    });

    return {
      ...options,
      modifiers: withBoundary
    };
  };

  measureScrollOffset(): number {
    if (this.expandable) {
      return this.expandableViewport?.nativeElement.scrollTop ?? 0;
    }
    return this.viewport?.measureScrollOffset() ?? 0;
  }

  scrollToOffset(offset: number): void {
    if (this.expandable) {
      this.expandableViewport?.nativeElement.scrollTo({ top: offset });
      return;
    }
    this.viewport?.scrollToOffset(offset);
  }

  scrollToIndex(index: number): void {
    if (this.expandable) {
      const viewport = this.expandableViewport?.nativeElement;
      const row = viewport?.querySelector<HTMLElement>(`[data-row-index="${index}"]`);
      if (viewport && row) {
        viewport.scrollTo({ top: row.offsetTop - viewport.offsetTop });
      }
      return;
    }
    this.viewport?.scrollToIndex(index);
  }

  getRowId(item: any): any {
    return item?.[this.rowIdKey];
  }

  isExpanded(item: any): boolean {
    return this.expandedIds.has(this.getRowId(item));
  }

  toggleExpanded(item: any): void {
    const id = this.getRowId(item);
    if (this.expandedIds.has(id)) {
      this.expandedIds.delete(id);
    } else {
      this.expandedIds.add(id);
    }
  }

  getSubRows(item: any): any[] {
    return item?.[this.subRowsKey] ?? [];
  }

  getSubRowActions(subRow: any): ComplianceTasksTableRowAction[] {
    return subRow?.[this.subRowActionsKey] ?? [];
  }

  getRowActions(item: any): ComplianceTasksTableRowAction[] {
    return item?.actions ?? [];
  }

  onExpandableScroll(event: Event): void {
    this.closeDropdowns();

    if (this.isLoading || this.isLoadingMore || this.isRefreshing) {
      return;
    }

    const target = event.target as HTMLElement;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= this.expandableScrollThreshold) {
      this.scrolledDown.emit();
    }
  }

  public onSearchTermUpdated(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.searchChanged.emit(searchTerm);
  }

  onComplianceStatusFilter(complianceStatus: ComplianceStatusFilter) {
    this.selectedComplianceStatus = complianceStatus;
    this.statusFilterChanged.emit(complianceStatus);
  }

  onRowClick(item: any) {
    this.rowClicked.emit(item);
  }

  getValue(item: any, key: string) {
    return item[key];
  }

  hasDisplayValue(item: any, key: string): boolean {
    const value = this.getValue(item, key);
    return value !== null && value !== undefined && value !== '';
  }

  getDisplayValue(item: any, key: string): string {
    const value = this.getValue(item, key);
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  isMutedValue(item: any, col: TableColumn): boolean {
    return !!col.mutedValue && this.getDisplayValue(item, col.value) === col.mutedValue;
  }

  hasLinkValue(item: any, col: TableColumn): boolean {
    return !!col.linkValue && this.hasDisplayValue(item, col.linkValue);
  }

  onLinkClick(event: Event, item: any, col: TableColumn): void {
    event.stopPropagation();
    this.linkClicked.emit({ item, column: col });
  }

  getTitleValue(item: any, col: TableColumn): string | null {
    if (!col.title) return null;
    const value = this.getValue(item, col.value);
    return value == null || value === '' ? null : String(value);
  }

  getStatusValue(item: any, key: string): string {
    const value = this.getValue(item, key);
    if (value == null) return '-';

    const labelKey = this.statusLabelKeys[value as ComplianceStatus] ?? this.textStatusLabelKeys[String(value)];
    return labelKey ? this.transloco.translate(labelKey) : value;
  }

  getStatusClass(item: any, key: string): string {
    const value = this.getValue(item, key);
    if (value == null) return '-';

    const rawLabel = this.statusLabels[value as ComplianceStatus] ?? value;
    return String(rawLabel).toLowerCase().replace(/\s+/g, '-');
  }

  isComplete(item: any, key: string): boolean {
    return this.getValue(item, key) === ComplianceStatus.Completed;
  }

  onActionClick(
    event: Event,
    item: any,
    action: ComplianceTasksTableAction
  ): void {
    event.stopPropagation();

    switch (action) {
      case ComplianceTasksTableAction.Assign: this.assignClicked.emit(item); break;
      case ComplianceTasksTableAction.AssignWorkshop: this.assignWorkshopClicked.emit(item); break;
      case ComplianceTasksTableAction.Archive: this.archiveClicked.emit(item); break;
      case ComplianceTasksTableAction.Restore: this.restoreClicked.emit(item); break;
      case ComplianceTasksTableAction.History: this.historyClicked.emit(item); break;
      case ComplianceTasksTableAction.EditRequirements: this.editRequirementsClicked.emit(item); break;
      case ComplianceTasksTableAction.EditFrequency: this.editFrequencyClicked.emit(item); break;
      case ComplianceTasksTableAction.Edit: this.editClicked.emit(item); break;
      case ComplianceTasksTableAction.Reschedule: this.rescheduleClicked.emit(item); break;
      case ComplianceTasksTableAction.ReschedulePre: this.reschedulePreClicked.emit(item); break;
      case ComplianceTasksTableAction.ReschedulePost: this.reschedulePostClicked.emit(item); break;
      case ComplianceTasksTableAction.Delete: this.deleteClicked.emit(item); break;
    }
  }

  onDocumentActionClick(event: Event, item: any, action: ComplianceTasksTableAction): void {
    event.stopPropagation();
    this.actionClicked.emit({ item, action });
  }

  hasPreLeg(item: RoutineComplianceTask): boolean {
    return resolveInspectionTypeId(item.routineTypeId, 'pre') !== null;
  }

 hasPostLeg(item: RoutineComplianceTask): boolean {
    return resolveInspectionTypeId(item.routineTypeId, 'post') !== null;
  }

  isRoutineComplete(item: RoutineComplianceTask): boolean {
    const hasPre = this.hasPreLeg(item);
    const hasPost = this.hasPostLeg(item);
    if (!hasPre && !hasPost) return false;

    const preDone = !hasPre || item.preComplianceStatus === ComplianceStatus.Completed;
    const postDone = !hasPost || item.postComplianceStatus === ComplianceStatus.Completed;

    return preDone && postDone;
  }

  private isLegReschedulable(status: ComplianceStatus | null): boolean {
    return status === ComplianceStatus.Pending || status === ComplianceStatus.Overdue;
  }

  canReschedulePre(item: RoutineComplianceTask): boolean {
    return this.hasPreLeg(item) && this.isLegReschedulable(item.preComplianceStatus);
  }

  canReschedulePost(item: RoutineComplianceTask): boolean {
    return this.hasPostLeg(item) && this.isLegReschedulable(item.postComplianceStatus);
  }

  preRescheduleLabel(item: any): string {
    const titleKey = routineChecklistTabTitleKeys(routineChecklistEntityKindFromLabel(item?.checklistType)).pre;
    return this.transloco.translate('compliance.shared.tasksTable.actions.rescheduleNamed', {
      name: this.transloco.translate(titleKey)
    });
  }

  postRescheduleLabel(item: any): string {
    const titleKey = routineChecklistTabTitleKeys(routineChecklistEntityKindFromLabel(item?.checklistType)).post;
    return this.transloco.translate('compliance.shared.tasksTable.actions.rescheduleNamed', {
      name: this.transloco.translate(titleKey)
    });
  }

  onCheckboxChange(event: Event, item: any, field: string): void {
    const checkbox = event.target as HTMLInputElement;
    item[field] = checkbox.checked;
    this.checkboxChanged.emit(item);
  }

  closeDropdowns(): void {
    this.dropdowns?.forEach((dropdown) => dropdown.close());
  }

  onScrollIndexChange(firstIndex: number): void {
    this.closeDropdowns();

    if (!this.isLoading && firstIndex > 0 && firstIndex + 10 >= this.data.length) {
      this.scrolledDown.emit();
    }
  }
}